import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Text,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  Textarea,
  VStack,
  AlertTitle,
  Select,
  Input,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FilesList } from "components/elements/DocumentsUpload";
import MoneyField from "components/elements/MoneyInput";
import moment from "moment";
import { DrawerFormField } from "pages/Borrowers/elements/AddBorrower";
import { AlertsContext } from "providers/alerts";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useCallback, useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { apiRequest, uploadDocument } from "utils/api";
import { ACCEPTED_FILE_TYPES, DRAW_REQUEST_SECTIONS } from "utils/constants";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { checkUiConfiguration, getCustomButtonVariant } from "utils/uiconfighelper";

const BoxTitleText = ({ title }: { title: string }) => (
  <Text color="#222222" fontWeight="500" fontSize="14px" lineHeight="20px">
    {title}
  </Text>
);

const BoxText = ({ text }: { text: string }) => (
  <Text color="secondary" fontSize="16px" lineHeight="28px">
    {text}
  </Text>
);

const EditDrawItem = () => {
  const { user } = useTenant();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { refetch: loadLoan, drawRequest, setDrawRequest } = useContext(LoanContext);
  const { showSuccess, showError } = useContext(AlertsContext);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [declined, setDeclined] = useState(false);

  const [reason, setReason] = useState("");
  const [readLoan, setReadLoan] = useState(false);
  const [drawPermission, setDrawPermission] = useState(false);
  const marginTop = drawRequest.status === "unpaid" ? "13.5rem" : "16rem";

  const [editItem, setEditItem] = useState<{
    title?: string;
    due_date?: string;
    amount?: string;
    section?: number;
    status?: string;
  }>({});

  const [editing] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: any[]) => setDocuments(acceptedFiles.concat(documents)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
  });

  const saveItem = async (newStatus?: "unpaid" | "pending" | "approved" | "declined" | "paid") => {
    try {
      setLoading(true);

      const addDocuments = documents.filter((document: any) => !document.id);
      const removeDocuments = drawRequest.documents.filter((document: any) =>
        documents.find((d: any) => d.id === document.id) ? false : true
      );

      for (const file of addDocuments) {
        await uploadDocument({
          file,
          draw_request: drawRequest.id,
          loan_id: drawRequest.loan,
        });
      }

      await apiRequest("/supabase/documents/delete", {
        ids: removeDocuments.map((document: any) => document.id),
      });

      const formattedDueDate = moment(editItem.due_date).format("YYYY-MM-DD"); //To send the formatted date

      await apiRequest("/supabase/draw_requests/update", {
        id: drawRequest.id,
        status: newStatus || editItem.status,
        amount: parseFloat(editItem.amount || ""),
        due_date: formattedDueDate,
        loan: drawRequest.loan,
      });

      if (newStatus === "paid" && drawRequest.status === "approved") {
        await apiRequest("/supabase/payments/insert", {
          draw_request: drawRequest.id,
        });
      }

      setDrawRequest(null);

      await loadLoan();

      showSuccess({
        title: "Draw Request Updated",
        description: "Your changes to the draw request have been saved.",
      });

      // onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    if (drawRequest) {
      setDocuments(drawRequest.documents || []);
      setEditItem({
        title: drawRequest.title,
        amount: drawRequest.amount,
        section: drawRequest.section,
        due_date: drawRequest.due_date,
        status: drawRequest.status,
      });
    }
    if (drawRequest) {
      onOpen();
    } else {
      onClose();
    }
  }, [drawRequest]);

  const readOnlyLoan = () => {
    if (isPermissionEnabled(user) && hasPermission(user, permissionConstants.approveDraw150k)) {
      setReadLoan(true);
    }
  };

  const handleMoneyInput = (val: any) => {
    const numericAmount = Number(val);
    if (readLoan) {
      if (numericAmount <= 150000) {
        setDrawPermission(false);
        setEditItem({
          ...editItem,
          amount: val,
        });
      } else {
        setDrawPermission(true);
      }
    } else {
      setEditItem({
        ...editItem,
        amount: val,
      });
    }
  };

  const checkButtonVisibility = (): any => {
    if (!checkUiConfiguration(user, "delete_draw_request")) {
      return "none";
    }
  };

  const deleteDraw = async () => {
    if (hasPermission(user, permissionConstants.deleteDraw)) {
      try {
        await apiRequest("/supabase/draw_requests/delete", {
          id: drawRequest.id,
        });
        loadLoan();
        showSuccess({
          title: "Draw Request Deleted",
          description: "Draw Request has been deleted successfully",
        });
      } catch (error) {
        console.error(error);
      } finally {
        onClose();
      }
    } else {
      showError({
        title: "Insufficient Permission",
        description: "You do not have permission to delete this draw request",
      });
    }
  };

  useEffect(() => {
    readOnlyLoan();
  }, []);

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      onCloseComplete={() => setDrawRequest(null)}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Draw Request</DrawerHeader>
        <DrawerBody>
          <Text fontSize="14px">
            Upload all receipts or other documents related to this draw for it to be processed and
            approved.
          </Text>
          {user.type === "lender" && (
            <Box textAlign="right">
              {/* <Button
                variant="ghost"
                size="sm"
                py="0"
                h="fit-content"
                color="primary">
                Edit Details
              </Button> */}
            </Box>
          )}
          {editing ? (
            <Box>
              <DrawerFormField
                label="Draw Title"
                placeholder="Draw Item"
                value={editItem.title || ""}
                onChange={val =>
                  setEditItem({
                    ...editItem,
                    title: val,
                  })
                }
                type="string"
                cy="edit-draw-request-title"
              />
            </Box>
          ) : (
            <Box
              mt="18px"
              mb="20px"
              borderWidth="1px"
              borderColor="gray.50"
              borderRadius="10px"
              py="20px"
              px="24px"
            >
              <Flex justifyContent="space-between">
                <Box flex="1">
                  <BoxTitleText title="Draw Title" />
                  <BoxText text={drawRequest.title} />
                </Box>
                <Box flex="1">
                  <BoxTitleText title="Draw Section" />
                  <BoxText text={DRAW_REQUEST_SECTIONS[drawRequest.section]} />
                </Box>
              </Flex>
              <Divider my="20px" />
              <Flex justifyContent="space-between" alignItems="top" gap="24px">
                <Box flexGrow="1">
                  <BoxTitleText title="Draw Amount" />
                  <FormControl>
                    <MoneyField
                      value={editItem.amount}
                      onValueChange={val => handleMoneyInput(val ?? "")}
                    />
                  </FormControl>
                </Box>
                <Box flexGrow="1">
                  <BoxTitleText title="Due Date" />
                  <FormControl display="flex" justifyContent="space-between" width="100%">
                    <Input
                      type="date"
                      value={editItem.due_date}
                      onChange={ev =>
                        setEditItem({
                          ...editItem,
                          due_date: ev.target.value,
                        })
                      }
                      variant="primary"
                    />
                  </FormControl>
                </Box>
              </Flex>
              {user.type === "borrower" && (
                <Text fontSize="13px" color="gray.300" mt="20px">
                  Changes done to the draw amount or due date will be reviewed and may require
                  additional documentation.
                </Text>
              )}
            </Box>
          )}
          {user.type === "borrower" && drawRequest.documents.length === 0 && (
            <Alert
              status="info"
              variant="subtle"
              alignItems="center"
              bg="gray.50"
              borderRadius="9px"
              mb="12px"
            >
              <AlertIcon boxSize="18px" mr="10px" color="gray.300" />
              <AlertDescription maxWidth="sm" fontSize="14px" color="secondary">
                Upload documents to this draw request to submit.
              </AlertDescription>
            </Alert>
          )}
          {drawRequest.status === "declined" && (
            <Alert
              status="error"
              mt="20px"
              // variant="subtle"
              alignItems="center"
              borderRadius="9px"
            >
              <AlertIcon boxSize="18px" mr="10px" color="gray.300" />
              <VStack alignItems="flex-start" spacing="0px">
                <AlertTitle fontSize="14px">Draw Request Declined</AlertTitle>
                <AlertDescription fontSize="14px" color="secondary">
                  {drawRequest.reason}
                </AlertDescription>
              </VStack>
            </Alert>
          )}
          {user.type === "lender" && (
            <FormControl mb="20px" display="flex" alignItems="center">
              <FormLabel mr="24px" mb="0px">
                Status
              </FormLabel>
              <Select
                value={editItem.status}
                onChange={ev =>
                  setEditItem({
                    ...editItem,
                    status: ev.target.value,
                  })
                }
              >
                <option value="unpaid">Unpaid</option>
                <option value="pending">Pending</option>
                <option value="declined">Declined</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
              </Select>
            </FormControl>
          )}
          <Box borderWidth="1px" borderColor="gray.50" borderRadius="10px" py="20px" px="24px">
            <FormControl mb="20px">
              <FormLabel>Documents</FormLabel>
              {documents.length === 0 ? (
                <Center h="60px">
                  <Text fontSize="13px" color="gray.300">
                    No documents added yet.
                  </Text>
                </Center>
              ) : (
                <FilesList files={documents} setFiles={setDocuments} />
              )}

              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Button
                  variant="ghost"
                  color="primary"
                  size="sm"
                  pl="0px"
                  onClick={open}
                  leftIcon={<FontAwesomeIcon icon={["far", "paperclip-vertical"]} />}
                >
                  {isDragActive ? "Drop" : "Upload"} document
                </Button>
              </div>
              <HStack justifyContent="space-between" color="gray.300" fontSize="13px" mt="10px">
                <Text>Accepted: .pdf, .jpg, .png, .csv, .xlsx</Text>
                <Text>Max size: 500 MB</Text>
              </HStack>
            </FormControl>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            marginTop={marginTop}
            style={{ display: checkButtonVisibility() }}
          >
            <span>
              <FontAwesomeIcon
                icon={["far", "trash"]}
                style={{ marginLeft: "5px", color: "red" }}
              />
            </span>
            <Button
              background={"none"}
              color={"red"}
              border={"none"}
              _hover={{ borderBottom: "2px solid red", color: "red" }}
              onClick={deleteDraw}
              isLoading={loading}
            >
              Delete Draw
            </Button>
          </Box>
        </DrawerBody>
        {user.type === "lender" ? (
          <DrawerFooter bg="#F8F8F8" boxShadow="2px -1px 5px 1px rgba(194, 194, 194, 0.35)">
            <Flex w="100%" justifyContent="space-between">
              <Button
                variant="outline"
                mr={3}
                onClick={() => setDrawRequest(null)}
                isLoading={loading}
              >
                Close
              </Button>
              <Button
                variant={getCustomButtonVariant(user)}
                onClick={() => saveItem()}
                isLoading={loading}
                isDisabled={drawPermission}
              >
                Save Changes
              </Button>
            </Flex>

            {drawRequest.status === "pending" && (
              <VStack flex="1" mt={4}>
                {declined && (
                  <FormControl mb="20px">
                    <FormLabel>Reason for Decline</FormLabel>
                    <Textarea
                      value={reason}
                      onChange={ev => setReason(ev.target.value)}
                      placeholder="Enter why the draw request was declined."
                    />
                  </FormControl>
                )}

                {declined ? (
                  <Flex w="100%" justifyContent="space-between">
                    <Button
                      variant="outline"
                      mr={3}
                      onClick={() => setDeclined(false)}
                      isLoading={loading}
                    >
                      Cancel
                    </Button>
                    <Button onClick={() => saveItem("declined")} isLoading={loading}>
                      Submit Decline
                    </Button>
                  </Flex>
                ) : (
                  <Flex w="100%" justifyContent="space-between">
                    <Button
                      variant="outline"
                      mr={3}
                      onClick={() => setDeclined(true)}
                      isLoading={loading}
                    >
                      Decline Draw
                    </Button>
                    <Button onClick={() => saveItem("approved")} isLoading={loading}>
                      Approve Draw
                    </Button>
                  </Flex>
                )}
              </VStack>
            )}
            {drawRequest.status === "approved" && (
              <Flex w="100%" justifyContent="flex-end" mt={4}>
                <Button onClick={() => saveItem("paid")} isLoading={loading}>
                  Mark as Paid
                </Button>
              </Flex>
            )}
          </DrawerFooter>
        ) : (
          <DrawerFooter bg="#F8F8F8" boxShadow="2px -1px 5px 1px rgba(194, 194, 194, 0.35)">
            <Flex w="100%" justifyContent="space-between">
              <Button
                variant="outline"
                mr={3}
                onClick={() => setDrawRequest(null)}
                isLoading={loading}
              >
                Close
              </Button>
              <Button
                variant={getCustomButtonVariant(user)}
                onClick={() => saveItem()}
                isLoading={loading}
              >
                Save Changes
              </Button>
            </Flex>

            {(drawRequest.status === "unpaid" || drawRequest.status === "declined") && (
              <Flex w="100%" justifyContent="flex-end" mt={4}>
                <Button
                  variant={getCustomButtonVariant(user)}
                  onClick={() => saveItem("pending")}
                  isLoading={loading}
                >
                  Submit Request
                </Button>
              </Flex>
            )}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default EditDrawItem;
